import { Button as ChakraButton, useRecipe } from '@chakra-ui/react'
import { clsx } from 'clsx'
import { forwardRef } from 'react'

import { buttonRecipe } from 'theme/recipes/button.recipe'

import type { RecipeVariantProps } from '@chakra-ui/react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'

type ButtonRecipeVariants = RecipeVariantProps<typeof buttonRecipe>

export type ButtonVariant = NonNullable<ButtonRecipeVariants['variant']>
export type ButtonSize = NonNullable<ButtonRecipeVariants['size']>

interface ButtonProperties extends ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean
  children: ReactNode
  iconOnly?: boolean
  isLoading?: boolean
  size?: ButtonSize
  variant?: ButtonVariant
}

export const Button = forwardRef<HTMLButtonElement, ButtonProperties>(
  (
    {
      asChild = false,
      children,
      className,
      disabled,
      iconOnly = false,
      isLoading = false,
      size = 'md',
      type = 'button',
      variant = 'primary',
      ...properties
    },
    reference
  ) => {
    /* --- Section 1: Hooks --- */
    const recipe = useRecipe({ recipe: buttonRecipe })

    /* --- Section 3: Derived Values --- */
    const isDisabled = isLoading || disabled === true
    const styles = recipe({ variant, size, iconOnly: iconOnly || undefined })

    /* --- Section 6: Render --- */
    return (
      <ChakraButton
        {...properties}
        asChild={asChild}
        className={clsx(className)}
        css={styles}
        data-icon-only={iconOnly ? 'true' : 'false'}
        data-size={size}
        data-variant={variant}
        disabled={asChild ? undefined : isDisabled}
        loading={isLoading}
        minW={iconOnly ? '[auto]' : undefined}
        ref={reference}
        type={asChild ? undefined : type}
      >
        {children}
      </ChakraButton>
    )
  }
)

Button.displayName = 'Button'
